import type { ReactNode } from 'react';

// Brand-aligned tooltip (s162.28). Replaces the browser-native `title`
// attribute on hover-revealable hints across the dashboard. Pure CSS
// — no portal, no positioning JS — so the wrapper is cheap to drop
// in around any inline trigger (button, link, chip).
//
// Visual: ink chat-bubble with cream text + 1.5px cream border +
// spell-yellow drop shadow. Matches mag's chat-bubble chrome on the
// dark side of the app so the tooltip feels like "mag whispering an
// aside" rather than a browser-generic gray pill.
//
// Limitations:
// - Positions relative to the trigger via absolute. If a parent has
//   overflow:hidden the tooltip can be clipped — wrap the trigger in
//   a known non-clipping ancestor or upgrade to a portal-based
//   variant if that becomes a problem.
// - Always renders below the trigger; no `placement` prop yet. The
//   simple case is enough for the current usages (action buttons,
//   chips). Bump to a portal + computed placement when we hit a
//   real near-edge case.
// - No keyboard support (the trigger's aria-label still carries the
//   accessible description; the visual tooltip is a hover affordance
//   only).
//
// Usage:
//   <Tooltip label="mark as read — fades the card">
//     <button onClick={onAck}>got it</button>
//   </Tooltip>

export interface TooltipProps {
  /** Short hint shown in the bubble. Wraps to multiple lines at
   *  ~260px so longer sentences don't run off-screen. */
  label: string;
  /** The hover trigger — typically a <button>, <a>, or chip. */
  children: ReactNode;
  /** Layout of the wrapper. Defaults to inline-block which preserves
   *  most button rows; switch to 'block' or 'contents' if you need
   *  the trigger to flow as a block element. */
  display?: 'inline-block' | 'block' | 'contents';
  /** Extra classes merged onto the wrapper. Use when the trigger is a
   *  flex child whose content must truncate — pass `min-w-0` so the
   *  wrapper can shrink below its content and the inner `truncate`
   *  actually clips (a bare block flex child won't shrink). */
  className?: string;
}

export function Tooltip({ label, children, display = 'inline-block', className }: TooltipProps) {
  const base =
    display === 'block'
      ? 'group/tip relative block'
      : display === 'contents'
        ? 'group/tip contents'
        : 'group/tip relative inline-block';
  const containerClass = className ? `${base} ${className}` : base;
  return (
    <span className={containerClass}>
      {children}
      {/* Render the bubble even on first paint so the css transition
          (opacity + translate) gives it a soft fade-in instead of a
          hard appear. pointer-events:none means the bubble itself
          never intercepts mouse — hover hand-off back to the trigger
          stays clean. */}
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 hidden w-max max-w-[260px] -translate-x-1/2 translate-y-1 whitespace-normal rounded-md border-[1.5px] border-[var(--cream)]/40 bg-[var(--ink)] px-2.5 py-1.5 text-left text-[11.5px] font-normal leading-[1.45] text-[var(--cream)] opacity-0 shadow-[3px_3px_0_var(--spell)] transition-all duration-150 group-hover/tip:block group-hover/tip:translate-y-0 group-hover/tip:opacity-100 group-focus-within/tip:block group-focus-within/tip:translate-y-0 group-focus-within/tip:opacity-100"
      >
        {label}
      </span>
    </span>
  );
}
